import {
    ApolloClient,
    ApolloLink,
    createHttpLink,
    from,
    InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { IncomingEventType } from '@thoughtspot/embed-util';
import { getCurrentLocale } from '@thoughtspot/i18n';
import { generateUUID } from '@thoughtspot/js-util';
import {
    getFormatLocales,
    getTseSchedulerClientId,
    isCallosumDebugInfoEnabled,
    isOrgPerUrlEnabled,
} from '@thoughtspot/session-service';
import _ from 'lodash';
import {
    FLAGS,
    flags as envFlags,
} from '/@services/system/config-service/flags-service';
import {
    getSubDomainIfOrgEnabled,
    getUrlHash,
} from '/@services/system/nav-service/nav-service';
import { EmbedEventErrorDetail } from '/@utils/embed.util';
import {
    embedErrorCodes,
    eventErrorType,
    EventType,
} from '/@utils/embed-base.util';
import {
    dispatchNonMemoEvent,
    subscribe,
} from '/@utils/event-bridge/event-bridge';
import { shouldFetchComsToken } from './apollo-client.util';
import { getValidComsToken } from './microfrontends/coms';
import { PinboardSerializingLink } from './services/pinboard/pinboard-serializing-link';
import { isRunningUnitTest } from './utils/testing/is-test';

enum ClientTypes {
    Blink,
    Mobile,
    FullEmbed,
    PublicApiDirect,
    BlinkV2,
}

const cache = new InMemoryCache({
    // Apollo uses Math.pow(2, 16) as a cache size for the computed outputs.
    // On a heavy liveboard, this fills up with just 5 vizzes, and any more loads keep re-writing the
    // cache and doesn't take any advantage of maintaining the cache.
    // Increase the limit to Math.pow(2, 20), but this can be adjusted later with more experiments.
    resultCacheMaxSize: 2 ** 20,
    // Apollo 3.13.5 defaults canonizeResults to false. Without canonization,
    // every cache re-read after a cache.modify creates new object references
    // for ALL fields (even unchanged ones), which breaks React useEffect
    // dependency checks that rely on reference equality — causing chart
    // destroy/recreate cycles and visible label flickering.
    canonizeResults: true,
});

const requestIDAdder = new ApolloLink((operation, forward) => {
    let requestId = '';

    if (operation.variables?.networkRequestId) {
        requestId = operation.variables.networkRequestId;
    } else {
        requestId = generateUUID();
    }

    operation.setContext(({ headers = {} }) => ({
        headers: {
            ...headers,
            'X-Request-Id': requestId,
            'X-ThoughtSpot-Request-Context': getUrlHash(),
            'X-Callosum-Client-Type': ClientTypes.BlinkV2,
            'X-ThoughtSpot-Client-Id': getTseSchedulerClientId(),
            'X-Session-Check': operation?.operationName === 'isSessionActive',
        },
    }));
    return forward(operation);
});

const comsTokenAdder = setContext(
    async ({ operationName }, { headers = {} }) => {
        if (!shouldFetchComsToken(operationName)) {
            return { headers };
        }
        const comsToken = await getValidComsToken();
        return {
            headers: {
                ...headers,

                // X-Coms-Token is used as a temporary measure until IAMV2 is fully available.  See
                // https://thoughtspot.atlassian.net/wiki/spaces/COMS/pages/2619277542/COMS+with+IAMV2+Auth for more details.
                ...(comsToken && { 'X-Coms-Token': comsToken }),
            },
        };
    },
);

// This is the standard header to use in requests to indicate the preferred locale
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language
const userLocaleAdder = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
        headers: {
            ...headers,
            'Accept-Language': getCurrentLocale(),
        },
    }));
    return forward(operation);
});

const userDataFormatAdder = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
        headers: {
            ...headers,
            'X-Date-Format-Locale': getFormatLocales().dateFormatLocale,
        },
    }));
    return forward(operation);
});

// Adds the current timestamp to the request
const requestTimestampAdder = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
        headers: {
            ...headers,
            'X-Request-Timestamp': new Date().toISOString(),
        },
    }));
    return forward(operation);
});

const enableCallosumDebugAdder = new ApolloLink((operation, forward) => {
    if (isCallosumDebugInfoEnabled()) {
        operation.setContext(({ headers = {} }) => ({
            headers: {
                ...headers,
                'X-Enable-Callosum-Debug-Info': true,
            },
        }));
    }
    return forward(operation);
});

/* Include override headers */
const overridesAdder = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => {
        const overrideOrgId = envFlags.getValue(FLAGS.overrideOrgId.name);
        const overriddenHeaders: any = {};
        /* Checks primary(0)/non-primary org id */
        if (
            isOrgPerUrlEnabled() &&
            overrideOrgId !== -1 &&
            (overrideOrgId === 0 || overrideOrgId)
        ) {
            overriddenHeaders['x-thoughtspot-orgsoverride'] = overrideOrgId;
        }
        return {
            headers: {
                ...headers,
                ...overriddenHeaders,
            },
        };
    });
    return forward(operation);
});

const errorIntercept = onError(errors => {
    if (isRunningUnitTest()) {
        return;
    }
    const errorMessage =
        errors.graphQLErrors ||
        'An error occurred while making the request to the API, please check the operation name and details';
    const standardizedErrors: EmbedEventErrorDetail = {
        errorType: eventErrorType.Api,
        message: (errorMessage as unknown) as string[],
        code: embedErrorCodes.GRAPHQL_API_ERRORS,
        error: errors.graphQLErrors,
    };
    dispatchNonMemoEvent(EventType.Error, standardizedErrors);
});

const globalAbortController = new AbortController();

const customFetch = (uri: string, options: any) => {
    const { operationName } = JSON.parse(options.body);

    const promise = fetch(`${uri}?op=${operationName}`, {
        ...options,
        signal: AbortSignal.any([globalAbortController.signal, options.signal]),
    });
    return promise;
};

const httpLink = createHttpLink({
    fetch: customFetch,
    credentials: 'same-origin',
    uri: '/prism/',
});

// Add X-Disable-Analytics for dev mode
const disableAnalytics = new ApolloLink((operation, forward) => {
    const ANALYTICS_DISABLED_APIS = [
        'CreateConversation',
        'LoadExistingAnswerSession',
        'GetChartWithData',
        'GetRecentlyPinnedPinboard',
        'UpdateConvContext',
        'SendMessage',
        'UpdateMessage',
        'GetTableWithData',
        'SendFeedback',
        'DeleteMessage',
        'UpdateClientStateTip',
        'GetConvAssistUnsupportedKeywords',
    ];
    const isE2ETest = envFlags.getValue('e2e');
    const isDev = process.env.NODE_ENV === 'development';
    if (
        ANALYTICS_DISABLED_APIS.includes(operation.operationName) &&
        (isDev || isE2ETest)
    )
        operation.setContext(({ headers = {} }) => ({
            headers: {
                ...headers,
                'X-Disable-Analytics': 'true',
            },
        }));
    return forward(operation);
});

// Custom Abort Controller Link for Query APIs
const customAbortControllerLink = new ApolloLink((operation, forward) => {
    const operationType = (operation.query.definitions.find(
        def => def.kind === 'OperationDefinition',
    ) as any)?.operation;

    // Apply to both queries and mutations (not subscriptions)
    if (operationType === 'query' || operationType === 'mutation') {
        const context = operation.getContext();

        // Only add abort controller if not already provided
        if (!context.fetchOptions?.signal) {
            const abortController = new AbortController();

            operation.setContext({
                ...context,
                fetchOptions: {
                    ...context.fetchOptions,
                    signal: abortController.signal,
                },
                // Store abort controller for potential external access
                abortController,
            });
        }
    }

    return forward(operation);
});

export const client = new ApolloClient({
    cache,
    link: from([
        disableAnalytics,
        requestIDAdder,
        comsTokenAdder,
        userLocaleAdder,
        userDataFormatAdder,
        requestTimestampAdder,
        enableCallosumDebugAdder,
        overridesAdder,
        errorIntercept,
        // Removed customAbortControllerLink - let Apollo handle abort logic naturally
        // This prevents AbortErrors from rapid user interactions after 3.13.5 upgrade
        new PinboardSerializingLink(),
        httpLink,
    ]),
    resolvers: {},
    // Devtools only in dev: when enabled Apollo keeps an unbounded mutationStore.
    devtools: { enabled: process.env.NODE_ENV !== 'production' },
});

let unsubscribeEmbedDestroyed: (() => void) | null = null;

/**
 * Resync Apollo after a bfcache restore.
 *
 * Refetches ALL active queries, not just the ones the freeze aborted: after a
 * long freeze the idle queries' cached data is stale too. Named so teardown
 * can unregister it.
 */
export const resyncAfterRestore = (event: PageTransitionEvent) => {
    if (!event.persisted) {
        return;
    }

    client.reFetchObservableQueries().catch(() => {
        // Swallowed: each query reports its own failure to its own watchers.
    });
};

/**
 * Abort in-flight requests and stop the Apollo client's scheduler.
 *
 * Intentionally does NOT call `client.clearStore()`. Clearing the store fires
 * store-change notifications on every active watch query, which cascade into a
 * final React render in the dying iframe against an empty cache — producing
 * cache-miss warnings and TypeErrors caught by ErrorBoundary (SCAL-301821).
 * The browser GCs the cache together with the iframe anyway. Sign-out and
 * session-reset flows that need a clean slate still call `clearStore()` directly.
 *
 * `event.persisted` marks a bfcache freeze, not a death: the document comes
 * back on Back, so it is left untouched. `resyncAfterRestore` refreshes it.
 */
export const stopApolloClient = (event?: Event) => {
    if ((event as PageTransitionEvent | undefined)?.persisted) {
        // Frozen, not dying: an abort here fails callers on a living page.
        return;
    }

    globalAbortController.abort();
    client.stop();
    (window as any).client = null;
    (window as any).cache = null;

    if (unsubscribeEmbedDestroyed) {
        unsubscribeEmbedDestroyed();
    }

    window.removeEventListener('unload', stopApolloClient);
    window.removeEventListener('pagehide', stopApolloClient);
    window.removeEventListener('pageshow', resyncAfterRestore);
};

unsubscribeEmbedDestroyed = subscribe(
    IncomingEventType.EmbedDestroyed,
    (_: any, next: any) => {
        stopApolloClient();
        if (next) {
            next();
        }
    },
);

window.addEventListener('unload', stopApolloClient);
window.addEventListener('pagehide', stopApolloClient);
window.addEventListener('pageshow', resyncAfterRestore);

export { NetworkStatus } from '@apollo/client';

// For debugging purposes
(window as any).client = client;

// For debugging purposes
(window as any).cache = cache;
